/**
 * GuideFrame — the traffic-signalled capture guide overlay (INTAKE-08 §A1).
 *
 * The customer corrects BEFORE shooting: the guide outline(s) carry a state —
 *   neutral  = "line it up"          (no claim either way)
 *   warn     = a named, fixable cue  (--warning; e.g. too dark)
 *   ready    = "hold steady"         (--success) — ONLY when a real detector
 *              justifies it. The droom bundle has no face/document detector
 *              (INTAKE-09 §3.2), so today's callers only ever pass neutral/warn;
 *              'ready' exists for the day an honest on-device cue can earn it.
 *              Never fake a "looks good" cue (INTAKE-08 §A1).
 *
 * Motion discipline: state transitions colour over 150ms ease (CSS), and there
 * is NO pulsing/blinking on the customer side — motion here is reassurance,
 * never alarm. The overlay is decorative (aria-hidden): the SAME reason is
 * always ALSO given in words by the step's `.vp-hint` line (WCAG 1.4.1 —
 * colour is never the sole signal).
 */

import type { CSSProperties } from 'react';

export type GuideFrameState = 'neutral' | 'warn' | 'ready';

interface GuideFrameProps {
  state: GuideFrameState;
  /** Show the face oval; positions override the CSS defaults. */
  face?: CSSProperties | false;
  /** Show the document card box; positions override the CSS defaults. */
  card?: CSSProperties | false;
}

export function GuideFrame({ state, face = false, card = false }: GuideFrameProps): JSX.Element {
  return (
    <div className={`vp-frame gf-${state}`} aria-hidden="true">
      {face ? <div className="vp-guide-face gf-guide" style={face} /> : null}
      {card ? <div className="vp-guide-card gf-guide" style={card} /> : null}
    </div>
  );
}
